import {
  extractPushApiErrors,
  type PushApiError,
  type PushConfigError,
  type PushErrorType,
} from '@/features/push-credentials';
import { formatError } from '@/utils/format-error';
import { isEmptyString } from '@/utils/string';

/** The id tells apart identical failures, which are each dismissed separately. */
export type PushSettingsError = PushConfigError & { id: string };

export const SAVE_FAILED_MESSAGE = "The configuration couldn't be saved. Try again.";
export const REMOVE_FAILED_MESSAGE = "Push settings couldn't be removed. Try again.";
export const SANDBOX_FAILED_MESSAGE = "The sandbox setting couldn't be changed. Try again.";

export const FCM_ERROR_CODES_URL = 'https://firebase.google.com/docs/reference/fcm/rest/v1/ErrorCode';

/** The one error whose copy comes from the response rather than the message map. */
const FCM_INVALID_ARGUMENT = 'FCM_INVALID_ARGUMENT';

let nextErrorId = 0;

function unixSeconds(): number {
  return Math.floor(Date.now() / 1000);
}

/**
 * A configuration error clears earlier send-test errors, which were about the
 * configuration that just failed. Send-test errors leave configuration errors in place.
 */
export function appendPushErrors(
  current: readonly PushSettingsError[],
  type: PushErrorType,
  errors: readonly PushApiError[],
): PushSettingsError[] {
  const kept = type === 'config' ? current.filter((error) => error.type === 'config') : [...current];
  const updated = unixSeconds();

  return [
    ...kept,
    ...errors.map((error) => {
      nextErrorId += 1;
      return { ...error, updated, type, id: `push-error-${nextErrorId}` };
    }),
  ];
}

/**
 * A rejection with no API errors in it — a dropped connection, a response that
 * is not JSON, a file that could not be read — is still reported, in the words
 * the caller gives it.
 */
export function pushErrorsOrFallback(error: unknown, fallback: string): PushApiError[] {
  const errors = extractPushApiErrors(error);
  return errors.length > 0 ? errors : [{ detail: fallback, status: '' }];
}

export function hasConfigError(errors: readonly PushSettingsError[]): boolean {
  return errors.some((error) => error.type === 'config');
}

export function sortPushErrorsNewestFirst(errors: readonly PushSettingsError[]): PushSettingsError[] {
  return [...errors].sort((one, other) => other.updated - one.updated);
}

/** For the page header's remove action, which has no error banner of its own. */
export function firstPushErrorMessage(error: unknown): string | null {
  const [first] = extractPushApiErrors(error);
  if (first == null) {
    return null;
  }
  const message = formatError(first, { type: 'push' });
  return isEmptyString(message) ? null : message;
}

export function fcmDynamicErrorMessage(error: PushApiError): string | null {
  if (error.detail !== FCM_INVALID_ARGUMENT) {
    return null;
  }
  const message = error.meta?.['full_error_message'];
  return typeof message === 'string' && !isEmptyString(message) ? message : null;
}
